import { Mic, MicOff } from "lucide-react";

/**
 * VoiceControlsUI - Voice mode control dialogs and status indicators
 * Handles mic permissions, voice mode selection, and listening status
 */
const VoiceControlsUI = ({
  selectedVoice,
  setSelectedVoice,
  audioFeedbackEnabled,
  setAudioFeedbackEnabled,
  showMicPermissionDialog,
  setShowMicPermissionDialog,
  micPermissionGranted,
  requestMicPermission,
  showVoiceModeChoice,
  handleVoiceModeSelection,
  voiceInputMode,
  isListeningForWakeWord,
  startWakeWordListener,
  stopWakeWordListener,
  voiceTranscript
}) => {
  return (
    <div className="space-y-4">
      {/* Voice Selection - Miss/Mr. Birdie */}
      <div className="flex items-center justify-between">
        <span className="font-medium text-stone-700">
          {selectedVoice === 'nova' ? 'Miss Birdie' : 'Mr. Birdie'}
        </span>
        <div className="flex items-center gap-2">
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="px-3 py-1.5 border border-green-300 rounded-lg text-sm bg-white"
          >
            <option value="nova">Miss Birdie</option>
            <option value="onyx">Mr. Birdie</option>
          </select>
          <button
            onClick={() => setAudioFeedbackEnabled(!audioFeedbackEnabled)}
            className={`p-2 rounded-lg ${audioFeedbackEnabled ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}
            title={audioFeedbackEnabled ? "Audio On" : "Audio Off"}
          >
            🔊 {audioFeedbackEnabled ? 'On' : 'Off'}
          </button>
        </div>
      </div>

      {/* Microphone Permission Dialog */}
      {showMicPermissionDialog && !micPermissionGranted && (
        <div className="bg-gradient-to-br from-green-100 to-emerald-100 border-2 border-green-300 rounded-xl p-5 text-center">
          <Mic className="w-12 h-12 mx-auto mb-3 text-green-700" />
          <h4 className="font-bold text-green-900 mb-2">Microphone Access</h4>
          <p className="text-sm text-green-700 mb-4">
            I need access to your microphone to hear your scores. Is that okay?
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setShowMicPermissionDialog(false)}
              className="px-4 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50"
            >
              Not Now
            </button>
            <button
              onClick={requestMicPermission}
              className="px-4 py-2 text-sm font-bold text-white bg-green-700 rounded-lg hover:bg-green-800"
            >
              Allow Microphone
            </button>
          </div>
        </div>
      )}

      {/* Voice Mode Choice Dialog */}
      {showVoiceModeChoice && micPermissionGranted && (
        <div className="bg-gradient-to-br from-green-100 to-emerald-100 border-2 border-green-300 rounded-xl p-5 text-center space-y-4">
          <div>
            <span className="text-3xl">🎙️</span>
            <h4 className="font-bold text-green-900 mt-2">How would you like to enter scores?</h4>
            <p className="text-sm text-green-700 mt-1">
              Choose your preferred input method
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {/* Interactive / Hands-free option */}
            <button
              onClick={() => handleVoiceModeSelection('interactive')}
              className="p-4 bg-white border-2 border-green-300 rounded-xl text-left hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">🗣️</span>
                <div>
                  <p className="font-bold text-green-800">Hands-Free (Wake Word)</p>
                  <p className="text-xs text-green-600 mt-1">
                    Say "Par Seeker" anytime to enter scores. Great for the course!
                  </p>
                </div>
              </div>
            </button>

            {/* Tap to Record option */}
            <button
              onClick={() => handleVoiceModeSelection('tap')}
              className="p-4 bg-white border-2 border-green-300 rounded-xl text-left hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">👆</span>
                <div>
                  <p className="font-bold text-green-800">Tap to Record</p>
                  <p className="text-xs text-green-600 mt-1">
                    Tap the mic button to record each score. More battery friendly.
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Interactive Mode Instructions */}
      {voiceInputMode === 'interactive' && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 text-sm">
          <p className="text-green-800 font-bold mb-2">Say "Par Seeker" to wake me up!</p>
          <ul className="text-green-700 space-y-1 text-xs">
            <li>• <b>"Par Seeker"</b> - I'll ask how you want to enter scores</li>
            <li>• <b>"Par Seeker Score 5"</b> - Enter score for current hole</li>
            <li>• <b>"Par Seeker Save"</b> - Save your round</li>
          </ul>
        </div>
      )}

      {/* Tap to Record Instructions */}
      {voiceInputMode === 'tap' && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 text-sm">
          <p className="text-green-800 font-bold mb-2">Tap the mic button to record!</p>
          <ul className="text-green-700 space-y-1 text-xs">
            <li>• Tap the green mic button below</li>
            <li>• Say your score (e.g., "5" or "bogey")</li>
            <li>• I'll confirm and move to the next hole</li>
          </ul>
        </div>
      )}

      {/* Listening Status - only for interactive mode */}
      {micPermissionGranted && voiceInputMode === 'interactive' && (
        <div className={`flex items-center justify-center gap-2 p-3 rounded-xl ${
          isListeningForWakeWord 
            ? 'bg-green-100 text-green-700' 
            : 'bg-stone-100 text-stone-500'
        }`}>
          {isListeningForWakeWord ? (
            <>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium">Listening for "Par Seeker"...</span>
              <button
                onClick={stopWakeWordListener}
                className="ml-2 text-xs px-2 py-1 bg-red-500 text-white rounded-lg"
              >
                Stop
              </button>
            </>
          ) : (
            <>
              <MicOff className="w-4 h-4" />
              <span className="text-sm">Not listening</span>
              <button
                onClick={startWakeWordListener}
                className="ml-2 text-xs px-2 py-1 bg-green-700 text-white rounded-lg"
              >
                Start
              </button>
            </>
          )}
        </div>
      )}

      {/* Live Transcript Display */}
      {voiceInputMode === 'interactive' && isListeningForWakeWord && voiceTranscript && (
        <div className="bg-white border border-green-200 rounded-xl p-3">
          <p className="text-xs text-green-600 mb-1">I'm hearing:</p>
          <p className="text-sm font-medium text-green-800">"{voiceTranscript}"</p>
          <p className="text-xs text-stone-400 mt-1">Say "Par Seeker" to trigger a command</p>
        </div>
      )}
    </div>
  );
};

export default VoiceControlsUI;
